/**
 * Settings tab — admin_settings key/value editor + privacy posture dials.
 */
import { useState, useEffect } from 'react';
import { api } from '../../../api';
import ReasonField from '../../../components/ops_admin/ReasonField';

export default function OrgSettingsTab({ org, opsRole }) {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const canMutate = ['admin', 'owner'].includes(opsRole);

  useEffect(() => {
    api.get(`/ops_admin/orgs/${org.id}/settings`)
      .then(d => setSettings(d.settings || {}))
      .catch(e => setMsg(`Error: ${e.message}`))
      .finally(() => setLoading(false));
  }, [org.id]);

  async function handleSave() {
    if (!reason.trim()) { setMsg('Reason required'); return; }
    setSaving(true); setMsg(null);
    try {
      const d = await api.patch(`/ops_admin/orgs/${org.id}/settings`, { updates: edits, reason });
      setSettings(d.settings || {});
      setEdits({});
      setReason('');
      setMsg('Saved');
    } catch (e) { setMsg(`Error: ${e.message}`); } finally { setSaving(false); }
  }

  // Privacy posture section
  const [postureForm, setPostureForm] = useState({
    tier_1_default: org.tier_1_default,
    standing_grant_tier: org.standing_grant_tier,
    debug_grant_default_hours: org.debug_grant_default_hours,
    debug_grant_max_hours: org.debug_grant_max_hours,
    external_providers_allowed: !!org.external_providers_allowed,
  });
  const [postureReason, setPostureReason] = useState('');
  const [postureSaving, setPostureSaving] = useState(false);
  const [postureMsg, setPostureMsg] = useState(null);
  const [editingPosture, setEditingPosture] = useState(false);
  // Out-of-boundary providers: what the server holds (the org prop is not
  // re-read after a save), and the tick ops gives when turning it on.
  const [externalOn, setExternalOn] = useState(!!org.external_providers_allowed);

  // ── Access grants (Tier 1 gating, plans/PLATFORM_PUSH_2026-10-23.md §4) ──
  const [grants, setGrants] = useState(null);
  const [reqForm, setReqForm] = useState({ tier_access: 1, case_id: '', reason: '' });
  const [reqBusy, setReqBusy] = useState(false);
  const [reqMsg, setReqMsg] = useState(null);
  async function loadGrants() {
    try { setGrants((await api.get(`/ops_admin/orgs/${org.id}/grants`)).grants || []); }
    catch { setGrants([]); }
  }
  useEffect(() => { loadGrants(); }, [org.id]);
  async function requestAccess() {
    if (!reqForm.reason.trim()) { setReqMsg('A reason is required — the customer reads it.'); return; }
    setReqBusy(true); setReqMsg(null);
    try {
      await api.post(`/ops_admin/orgs/${org.id}/grant_requests`, {
        tier_access: Number(reqForm.tier_access),
        case_id: reqForm.case_id.trim() || null,
        reason: reqForm.reason.trim(),
      });
      setReqForm({ tier_access: 1, case_id: '', reason: '' });
      setReqMsg('Request sent. The customer admin sees it under Privacy & Audit and decides.');
      await loadGrants();
    } catch (e) {
      setReqMsg(e?.message || 'Could not send the request');
    } finally {
      setReqBusy(false);
    }
  }
  const gatingLine = !org.tier1_gating_on
    ? 'Tier 1 gating is OFF platform-wide (System tab) — names and file names are visible, every read logged.'
    : !org.tier1_qualifies
      ? (org.first_login_at
          ? 'This organization opted Tier 1 visible — names are not masked.'
          : 'Onboarding window: nothing is masked until the organization\'s first sign-in.')
      : org.tier1_gated
        ? 'Names and file names are MASKED for this organization — request access below.'
        : 'Unlocked for this request by an active customer grant.';
  const [statementConfirmed, setStatementConfirmed] = useState(false);
  const turningOnExternal = postureForm.external_providers_allowed && !externalOn;

  const postureDisabled = org.privacy_posture_locked || org.is_criminal_justice;

  async function handlePostureSave() {
    if (!postureReason.trim()) { setPostureMsg('Reason required'); return; }
    if (turningOnExternal && !statementConfirmed) {
      setPostureMsg('Confirm that the customer was given the statement and accepted it');
      return;
    }
    setPostureSaving(true); setPostureMsg(null);
    try {
      await api.patch(`/ops_admin/orgs/${org.id}/privacy`, {
        ...postureForm,
        ...(turningOnExternal ? { external_providers_statement_confirmed: true } : {}),
        reason: postureReason,
      });
      setExternalOn(postureForm.external_providers_allowed);
      setStatementConfirmed(false);
      setEditingPosture(false);
      setPostureReason('');
      setPostureMsg('Saved');
    } catch (e) { setPostureMsg(`Error: ${e.message}`); } finally { setPostureSaving(false); }
  }

  if (loading) return <p className="loading-text">Loading settings...</p>;

  return (
    <div className="ops-tab">
      <div className="ops-section">
        <h3>Privacy Posture</h3>
        {postureDisabled && (
          <p className="info-banner">
            {org.is_criminal_justice
              ? 'Criminal justice org — privacy posture is locked at maximum. Only owners can modify.'
              : 'Privacy posture is locked by an owner action.'}
          </p>
        )}
        <div className="posture-form">
          <div className="form-group">
            <label className="form-label">Tier 1 visible to ops by default (filenames, case names)</label>
            <select className="form-input" value={postureForm.tier_1_default ? 'true' : 'false'}
              disabled={!editingPosture || postureDisabled}
              onChange={e => setPostureForm(f => ({...f, tier_1_default: e.target.value === 'true'}))}>
              <option value="false">No (max privacy default)</option>
              <option value="true">Yes (allow)</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Standing grant tier</label>
            <select className="form-input" value={postureForm.standing_grant_tier}
              disabled={!editingPosture || postureDisabled}
              onChange={e => setPostureForm(f => ({...f, standing_grant_tier: parseInt(e.target.value)}))}>
              <option value={0}>0 — Per-case grants required</option>
              <option value={1}>1 — Standing Tier 1 (names visible)</option>
              <option value={2}>2 — Standing Tier 1+2 (full access, audited)</option>
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Default grant duration (hours)</label>
              <input type="number" className="form-input" value={postureForm.debug_grant_default_hours}
                disabled={!editingPosture || postureDisabled}
                onChange={e => setPostureForm(f => ({...f, debug_grant_default_hours: parseInt(e.target.value)}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Max grant duration (hours)</label>
              <input type="number" className="form-input" value={postureForm.debug_grant_max_hours}
                disabled={!editingPosture || postureDisabled}
                onChange={e => setPostureForm(f => ({...f, debug_grant_max_hours: parseInt(e.target.value)}))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Out-of-boundary AI providers (Claude via Vertex, AssemblyAI)</label>
            <select className="form-input" value={postureForm.external_providers_allowed ? 'true' : 'false'}
              disabled={!editingPosture || postureDisabled || org.is_criminal_justice}
              onChange={e => setPostureForm(f => ({...f, external_providers_allowed: e.target.value === 'true'}))}>
              <option value="false">Blocked — in-boundary providers only (default)</option>
              <option value="true">Allowed — chat answers come from Claude; every use is audited</option>
            </select>
            <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              {org.is_criminal_justice
                ? 'Criminal-justice org: permanently blocked (CJIS boundary).'
                : 'These providers run outside the CJIS-attested environment (Assured Workloads). With this on, the organization\'s chat is answered by Claude and says so under the chat box. Enable only for non-CJ customers who accept that.'}
            </p>
            {editingPosture && turningOnExternal && (
              <div className="info-banner" data-testid="external-providers-statement">
                <p style={{ margin: 0 }}><strong>Give the customer this statement before turning it on:</strong></p>
                <p style={{ margin: '6px 0' }}>{org.external_providers_statement}</p>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="checkbox" checked={statementConfirmed}
                    onChange={e => setStatementConfirmed(e.target.checked)} />
                  The customer was given this statement and accepted it (the wording is kept with the audit entry)
                </label>
              </div>
            )}
          </div>
        </div>

        {!editingPosture && canMutate && !postureDisabled && (
          <button className="btn btn-sm btn-secondary" onClick={() => setEditingPosture(true)}>Edit Posture</button>
        )}
        {editingPosture && (
          <div>
            <ReasonField value={postureReason} onChange={setPostureReason} />
            <div className="button-row">
              <button className="btn btn-primary" onClick={handlePostureSave} disabled={postureSaving}>
                {postureSaving ? 'Saving...' : 'Save Posture'}
              </button>
              <button className="btn btn-ghost" onClick={() => setEditingPosture(false)}>Cancel</button>
            </div>
          </div>
        )}
        {postureMsg && <p className="save-msg">{postureMsg}</p>}
      </div>

      <div className="ops-section" data-testid="tier1-grants">
        <h3>Access Grants (Tier 1 and 2)</h3>
        <p className="muted" style={{ marginTop: 0 }}>{gatingLine}</p>
        <table className="ops-table">
          <thead><tr><th>Tier</th><th>Case</th><th>Status</th><th>Expires</th><th>Reason</th></tr></thead>
          <tbody>
            {grants === null && <tr><td colSpan={5} className="muted">loading…</td></tr>}
            {grants && grants.length === 0 && <tr><td colSpan={5} className="muted">No grants for you on this organization.</td></tr>}
            {grants && grants.map(g => (
              <tr key={g.id}>
                <td>{g.tier_access}</td>
                <td>{g.case_id ? g.case_id.slice(0, 8) : 'org-wide'}</td>
                <td><span className="badge">{g.status}</span></td>
                <td>{g.expires_at ? new Date(g.expires_at).toLocaleString() : '—'}</td>
                <td className="muted">{g.reason_request}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {canMutate && (
          <div className="posture-form" style={{ marginTop: 12 }}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Tier</label>
                <select className="form-input" value={reqForm.tier_access}
                  onChange={e => setReqForm(f => ({ ...f, tier_access: e.target.value }))}>
                  <option value={1}>1 — names and file names</option>
                  <option value={2}>2 — content (documents, AI output, messages)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Case id (blank = organization-wide)</label>
                <input className="form-input" value={reqForm.case_id} placeholder="optional"
                  onChange={e => setReqForm(f => ({ ...f, case_id: e.target.value }))} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Reason the customer will read</label>
              <input className="form-input" value={reqForm.reason} placeholder="e.g. Support ticket #41: an upload is stuck and I need the file name"
                onChange={e => setReqForm(f => ({ ...f, reason: e.target.value }))} />
            </div>
            <button className="btn btn-sm btn-primary" onClick={requestAccess} disabled={reqBusy}>
              {reqBusy ? 'Sending…' : 'Request access'}
            </button>
            {reqMsg && <p className="save-msg">{reqMsg}</p>}
          </div>
        )}
      </div>

      <div className="ops-section">
        <h3>Admin Settings</h3>
        <table className="ops-table">
          <thead><tr><th>Key</th><th>Value</th>{canMutate && <th>Edit</th>}</tr></thead>
          <tbody>
            {Object.entries(settings).length === 0 && (
              <tr><td colSpan={3} className="empty-row">No settings</td></tr>
            )}
            {Object.entries(settings).map(([key, val]) => (
              <tr key={key}>
                <td className="mono">{key}</td>
                <td>
                  {canMutate && key in edits ? (
                    <input className="form-input form-input-sm" value={edits[key]}
                      onChange={e => setEdits(ed => ({...ed, [key]: e.target.value}))} />
                  ) : (
                    <span className="mono">{JSON.stringify(val)}</span>
                  )}
                </td>
                {canMutate && (
                  <td>
                    {key in edits ? (
                      <button className="btn btn-sm btn-ghost" onClick={() => setEdits(ed => { const n = {...ed}; delete n[key]; return n; })}>Reset</button>
                    ) : (
                      <button className="btn btn-sm btn-ghost" onClick={() => setEdits(ed => ({...ed, [key]: String(val)}))}>Edit</button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {canMutate && Object.keys(edits).length > 0 && (
          <div className="settings-save">
            <ReasonField value={reason} onChange={setReason} />
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        )}
        {msg && <p className="save-msg">{msg}</p>}
      </div>
    </div>
  );
}
